import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store, select } from '@ngrx/store';
import { ReportsState, getCache, DeleteArchive, LoadBrandsData, LoadLinescore, LoadVerbatims, LoadOther, LoadDriver, LoadRatings } from './store';
import { Subscription, Observable, of } from 'rxjs';
import { switchMap, map, filter, tap } from 'rxjs/operators';
import { getEnabled, EnableExport, DisableExport, ArchiveReport, getAllStaged } from './store';
import { MatDialog } from '@angular/material/dialog';
import { paths } from './report-paths';
import { ManySelectorComponent } from './many-selector/many-selector.component';
import { ReportCache, Client } from 'core-lib';
import { ClientService, ReportCacheService } from "core";
import { ViewportScroller, NgIf, NgFor } from '@angular/common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { TableOptionsComponent } from './table-options/table-options.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, RouterModule, Router, NavigationStart } from '@angular/router';
import { CollapseLinksComponent } from './collapse-links/collapse-links.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { DomSanitizer } from '@angular/platform-browser';
// import { TextBlockComponent } from 'core/components/storyblok';
import { MatRippleModule } from '@angular/material/core';
import { environment } from '../environments/environment';

@Component({
  selector: 'ls-reports',
  // changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: true,
  imports: [NgIf, MatCardModule, MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule, MatTabsModule, NgFor, CollapseLinksComponent, RouterModule, MatSidenavModule, MatListModule, MatRippleModule]
})
export class ReportsComponent implements OnInit, OnDestroy {

  @ViewChild('sidenav') reportsNav: MatSidenav;
  @Input() manual: any;

  exporting$: Observable<boolean>;
  items$: Observable<any[]>;
  itemCount: number;
  reportCache: ReportCache;
  clientSub: Subscription;
  client: Client;

  exporting: boolean = false;
  navLinks: { name: string, url: string, path: string }[];
  advToolsUrl = environment.advancedToolsUrl;

  // private deckId: string;

  // Layout detection
  private layoutSub: Subscription;
  isLarge: boolean = false;
  // TODO: Remove this after a dedicated view exists for clients
  hideStuff: boolean = false;
  mode: string = 'side';
  
  get returnPortalUrl(): string {
    return `${environment.advancedToolsUrl}/client/${this.client.client_id}/${this.reportCache.cell_id}`;
  }

  constructor(
    private store: Store<ReportsState>,
    private clientServ: ClientService,
    private dialog: MatDialog,
    public rs: ReportCacheService,
    private scroll: ViewportScroller,
    private bpObs: BreakpointObserver,
    private snackbar: MatSnackBar,
    private route: ActivatedRoute,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    router: Router
  ) {
    router.events.pipe(
      filter(ev => ev instanceof NavigationStart),
      takeUntilDestroyed()
    ).subscribe(ev => {
      console.log(ev);
      if (!this.isLarge)
        this.reportsNav.close();
    })
  }
  
  protected showHelp = (tabId: string, overrideItemId?: string) => {
    // TODO: Lots of change detection shown with this log. Fix
    // console.log('Got tab');

    return async (itemId: string) => {
      console.log('Open helper');
      // const tabContent = this.manual[tabId];
      // console.log(tabContent, itemId);

      const { HelperViewerComponent } = await import('./helper-viewer/helper-viewer.component');

      this.dialog.open(HelperViewerComponent, {
        data: {
          // helpContent: this.manual[tabId].blocks[overrideItemId || itemId || 'main'],
          blokId: tabId
        }
      });
    };
  }


  exportTables() {
    const cacheKey: string = this.reportCache.cache_key;
    return this.rs.exportTables(cacheKey).pipe(
      switchMap(result => {
        const link = document.createElement("a"); //create 'a' element
        link.setAttribute("href", result.spreadsheet); //replace "file" with link to file you want to download
        link.setAttribute("download", result.spreadsheet);// replace "file" here too
        link.click();
        return of(true);
      })
    ).subscribe(() => {
      console.log('Finished!');

    })
  }

  openTableDialog() {
    this.dialog.open(TableOptionsComponent, {
      data: { reportCache: this.reportCache }
    });
  }

  deleteArchive() {
    // do stuff
    this.store.dispatch(new DeleteArchive({ archiveId: this.reportCache.parameters.archive_id }));
  }

  slidesCancel(): void {
    this.store.dispatch(new DisableExport());
  }

  // TODO: Add modal dialog for name entry
  createArchive(): void {
    this.store.dispatch(new ArchiveReport(this.reportCache.cache_key));
  }

  reportChange(value: string): void {
    switch (value) {
      case 'TO_SLIDES': {
        this.store.dispatch(new EnableExport());
      }
    }
  }

  scrollMe(): void {
    this.scroll.scrollToPosition([0, 0]);
  }

  selectAll(type: 'chart' | 'linescale' | 'table') {
    this.dialog.open(ManySelectorComponent, {
      // height: '500px',
      // width: '550px'
      data: {
        cacheKey: this.reportCache.cache_key,
        type
      },
      hasBackdrop: true,
      disableClose: true
    }).afterClosed();
  }

  ngOnInit() {
    this.iconRegistry.addSvgIcon('ms-excel', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons8-excel_1.svg'));

    this.hideStuff = this.route.snapshot.queryParams.hs_;
    this.layoutSub = this.bpObs.observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
    ]).subscribe(result => {
      console.log('Changed size', result.breakpoints);
      
      this.isLarge = !result.matches;
    });

    this.items$ = this.store.pipe(
      select(getAllStaged),
      tap(items => this.itemCount = items.length)
    );
    this.clientSub = this.store.pipe(
      select(getCache),
      switchMap(reportCache => {
        this.reportCache = reportCache;
        this.navLinks = paths[reportCache.report_type];
        if (!reportCache.control_flags.has_competitors) {
          this.navLinks = this.navLinks.filter(path => path.name !== 'Competitors');
        }
        if (!this.reportCache.control_flags.has_pricing) {
          this.navLinks = this.navLinks.filter(path => path.path !=='vw');
        }

        if (this.hideStuff)
          this.navLinks = [];

        return this.clientServ.getClient(reportCache.client_id);
      })
    ).subscribe(client => {
      this.client = client;
      // this.changeRef.detectChanges();
    });
  }

  reRun() {
    const cache = this.reportCache;
    this.snackbar.open('Reloading data...');
    this.rs.tabulate(cache.cache_key, this.reportCache.report_type).subscribe(() => {
      this.store.dispatch(new LoadBrandsData(cache.cache_key));
      this.store.dispatch(new LoadLinescore(cache.cache_key));
      this.store.dispatch(new LoadVerbatims(cache.cache_key));
      this.store.dispatch(new LoadOther({ cacheKey: cache.cache_key, crosstab: cache.report_type === 'crosstab' }));
      this.store.dispatch(new LoadDriver(cache.cache_key));
      this.store.dispatch(new LoadRatings(cache.cache_key));
    });
  }

  ngOnDestroy() {
    this.clientSub.unsubscribe();
    this.layoutSub.unsubscribe();
  }

}

// if (typeof Worker !== 'undefined') {
//   // Create a new
//   const worker = new Worker('./reports.worker', { type: 'module' });
//   worker.onmessage = ({ data }) => {
//     console.log(`page got message: ${data}`);
//   };
//   worker.postMessage('hello');
// } else {
//   // Web Workers are not supported in this environment.
//   // You should add a fallback so that your program still executes correctly.
// }